import { ref, type MaybeRef, toValue, computed } from 'vue'
import { useUser } from '@gat-ship/user'
import { useAppLoading } from '@gat-ship/vue-components'
import { globalUtils } from '@gat-ship/utils'
import type {
  AiPromptAsset,
  PromptTarget,
  PromptTargetType
} from '../model/types'
import { useAiApiCalls } from '../model/api'
import { AI_PERMISSIONS } from '../constants'
import {
  usePromptAssetsStore,
  type UsePromptAssetsStoreOptions
} from '../state/prompt-assets-store'
import { useAiModelSelection } from './useAiModelSelection'

export interface UsePromptAssetsOptions {
  target: MaybeRef<PromptTarget>
  promptType: PromptTargetType
}

export function usePromptAssets(options: UsePromptAssetsOptions) {
  const target = toValue(options.target)
  const { promptType } = options

  const storeOptions: UsePromptAssetsStoreOptions = { target, promptType }
  const store = usePromptAssetsStore(storeOptions)

  const user = useUser()
  const { setLoadingId, removeLoadingId } = useAppLoading()

  const { addAssetRequest, updateAssetRequest, removeAssetRequest } =
    useAiApiCalls()

  const canManageAssets = computed(() =>
    user.hasPermission(AI_PERMISSIONS.manageAssets)
  )

  const { canOverrideModel, getAiModelOverride } = useAiModelSelection(
    store.selectedThinkingModel
  )

  const isAddPresetModalOpen = ref(false)
  const isEditPresetModalOpen = ref(false)
  const editingAsset = ref<AiPromptAsset | undefined>(undefined)

  function openAddPresetModal(asset?: AiPromptAsset) {
    const newAsset: AiPromptAsset = asset ?? {
      id: globalUtils.functions.uuid(),
      promptType,
      promptTargetId: target,
      displayName: 'New Preset',
      shortDescription: '',
      targetAiModel: 'default',
      defaultModel: 'default',
      promptText: '',
      isDefault: false
    }
    editingAsset.value = newAsset
    isAddPresetModalOpen.value = true
  }

  function openEditPresetModal(asset: AiPromptAsset) {
    editingAsset.value = { ...asset }
    isEditPresetModalOpen.value = true
  }

  async function ensureDefaultExists(excludeId?: string) {
    const all = [...store.assets.value]
    const filtered = excludeId ? all.filter((a) => a.id !== excludeId) : all
    const hasDefault = filtered.some((a) => a.isDefault)
    if (!hasDefault) {
      const systemPrompt = filtered.find((a) => a.id === target)
      if (
        systemPrompt &&
        !store.virtualAssets.value.some((a) => a.id === systemPrompt.id)
      ) {
        await updateAssetRequest({
          pathParams: { id: systemPrompt.id },
          body: {
            ...systemPrompt,
            isDefault: true,
            shortDescription: systemPrompt.shortDescription || null,
            promptText: systemPrompt.promptText ?? null
          }
        })
      }
    }
  }

  async function addAsset(asset: AiPromptAsset) {
    const loadingIds = [asset.id]
    try {
      setLoadingId(loadingIds)
      isAddPresetModalOpen.value = false
      await ensureDefaultExists()
      await addAssetRequest({
        body: {
          id: asset.id,
          promptType: asset.promptType,
          promptTargetId: target,
          displayName: asset.displayName,
          shortDescription: asset.shortDescription || null,
          targetAiModel: asset.targetAiModel,
          promptText: asset.promptText ?? null,
          isDefault: asset.isDefault
        }
      })
      store.fetchAssets()
    } catch (error) {
      editingAsset.value = asset
      isAddPresetModalOpen.value = true
      throw error
    } finally {
      removeLoadingId(loadingIds)
    }
  }

  async function updateAsset(asset: AiPromptAsset) {
    const loadingIds = [asset.id]
    try {
      setLoadingId(loadingIds)
      isEditPresetModalOpen.value = false
      await ensureDefaultExists()
      await updateAssetRequest({
        pathParams: { id: asset.id },
        body: {
          displayName: asset.displayName,
          shortDescription: asset.shortDescription || null,
          targetAiModel: asset.targetAiModel,
          promptText: asset.promptText ?? null,
          isDefault: asset.isDefault
        }
      })
      store.fetchAssets()
    } catch (error) {
      editingAsset.value = asset
      isEditPresetModalOpen.value = true
      throw error
    } finally {
      removeLoadingId(loadingIds)
    }
  }

  const showDeleteConfirm = ref(false)
  const pendingDeleteAsset = ref<AiPromptAsset>()

  function removeAsset(asset: AiPromptAsset) {
    pendingDeleteAsset.value = asset
    showDeleteConfirm.value = true
  }

  async function confirmDeleteAsset() {
    showDeleteConfirm.value = false
    const asset = pendingDeleteAsset.value
    if (!asset) return
    const loadingIds = [asset.id]
    try {
      isEditPresetModalOpen.value = false
      setLoadingId(loadingIds)
      await ensureDefaultExists(asset.id)
      await removeAssetRequest({
        pathParams: { id: asset.id }
      })
      store.fetchAssets()
    } catch (error) {
      editingAsset.value = asset
      isEditPresetModalOpen.value = true
      throw error
    } finally {
      removeLoadingId(loadingIds)
    }
  }

  return {
    target,
    assets: store.assets,
    virtualAssets: store.virtualAssets,
    selectedAssetId: store.selectedAssetId,
    selectedThinkingModel: store.selectedThinkingModel,
    getDefaultSystemPrompt: store.getDefaultSystemPrompt,
    canManageAssets,
    canOverrideModel,

    addAsset,
    updateAsset,
    removeAsset,
    showDeleteConfirm,
    pendingDeleteAsset,
    confirmDeleteAsset,

    isAddPresetModalOpen,
    isEditPresetModalOpen,
    editingAsset,
    openAddPresetModal,
    openEditPresetModal,

    getCustomPromptAssetId: store.getCustomPromptAssetId,
    getAiModelOverride,
    fetchAssets: store.fetchAssets
  }
}
